/**
 * Day/night lighting: a screen-space quad multiplied over the map (terrain → labels). Warm lamp
 * light by day; deep blue-grey at night with a pool of desk-lamp light towards the screen
 * centre. Units, orders and effects are drawn above it (lightly tinted instead) so the
 * situation stays readable at night and muzzle flashes / city lights glow.
 *
 * Weather: soft cloud shadows drift over the map with the weather cells (textured with noise
 * that travels with each cell), storms darken the country beneath them, and lightning briefly
 * lights a storm's heart (never under reduced motion).
 */
import { type Geometry, Mesh, type Shader, type Texture } from 'pixi.js';
import { destroyMesh, FRAG_HEADER, makeShader } from '../gfx/gl';
import { WORLD_QUAD_VERT } from '../shaders/terrain';
import { quadGeometry, setQuad } from './terrain';
import type { SkyField } from './weather';

const FRAG = /* glsl */ `${FRAG_HEADER}
in vec2 vWorld;
uniform vec2 uScreen;
uniform vec3 uLight;
uniform vec3 uLamp;
uniform float uNight;
uniform float uDim;
uniform float uVignette;
uniform vec3 uCam;
uniform vec4 uFlash;
uniform vec2 uDrift;
uniform float uWeather;
uniform sampler2D uNoise;
uniform sampler2D uSky;
void main() {
  vec2 uv = vWorld / uScreen;
  vec2 d = (uv - 0.5) * vec2(uScreen.x / uScreen.y, 1.0);
  float r = length(d);
  float pool = 1.0 - smoothstep(0.08, 0.95, r);
  vec3 L = mix(uLight, uLamp, pool * uNight * 0.62);
  float vig = 1.0 - smoothstep(0.3, 1.1, r) * uVignette * (0.22 + 0.25 * uNight);
  if (uWeather > 0.0) {
    vec2 w = uCam.xy + (vWorld - 0.5 * uScreen) / uCam.z;
    vec4 field = texture(uSky, uv);
    float cloud = field.r;
    float storm = smoothstep(0.45, 0.9, field.g);
    // The deck's billows drift with the wind like the systems themselves.
    if (cloud > 0.0) {
      vec4 n = texture(uNoise, (w - uDrift) * (1.0 / 520.0) + vec2(0.137, 0.29));
      cloud *= clamp(0.35 + 0.95 * n.g + 0.35 * (n.b - 0.5), 0.0, 1.0);
    }
    // Cool, blue-grey shade: overcast takes the warmth out of the paper first.
    L *= 1.0 - (0.15 * cloud * (1.0 - 0.4 * uNight) + 0.15 * storm) * vec3(1.1, 1.0, 0.72);
    if (uFlash.z > 0.0) {
      vec2 fd = w - uFlash.xy;
      L += vec3(0.75, 0.8, 0.95) * uFlash.z * exp(-dot(fd, fd) / (uFlash.w * uFlash.w));
    }
  }
  finalColor = vec4(L * vig * uDim, 1.0);
}
`;

interface Key {
  h: number;
  c: [number, number, number];
}

// Light colour through the day (hour → multiplier).
const KEYS: Key[] = [
  { h: 0, c: [0.4, 0.45, 0.6] },
  { h: 4.3, c: [0.42, 0.46, 0.6] },
  { h: 5.4, c: [0.78, 0.66, 0.68] },
  { h: 6.6, c: [0.98, 0.9, 0.8] },
  { h: 9, c: [1, 0.975, 0.93] },
  { h: 15, c: [1, 0.97, 0.92] },
  { h: 18, c: [1, 0.92, 0.8] },
  { h: 19.5, c: [0.86, 0.68, 0.6] },
  { h: 20.6, c: [0.46, 0.48, 0.62] },
  { h: 24, c: [0.4, 0.45, 0.6] },
];

/** Light multiplier and night factor (0 day … 1 night) for a fractional hour of day. */
export function lightAt(hour: number): { color: [number, number, number]; night: number } {
  const h = ((hour % 24) + 24) % 24;
  let i = 0;
  while (i < KEYS.length - 2 && KEYS[i + 1].h <= h) i++;
  const a = KEYS[i];
  const b = KEYS[i + 1];
  const t = (h - a.h) / (b.h - a.h);
  const s = t * t * (3 - 2 * t);
  const color: [number, number, number] = [
    a.c[0] + (b.c[0] - a.c[0]) * s,
    a.c[1] + (b.c[1] - a.c[1]) * s,
    a.c[2] + (b.c[2] - a.c[2]) * s,
  ];
  const lum = (color[0] + color[1] + color[2]) / 3;
  const night = Math.min(1, Math.max(0, (0.93 - lum) / 0.43));
  return { color, night };
}

export class LightLayer {
  readonly mesh: Mesh<Geometry, Shader>;
  private readonly shader: Shader;
  private w = 1;
  private h = 1;

  constructor(noise: Texture, sky: SkyField) {
    this.shader = makeShader('friction-light', WORLD_QUAD_VERT, FRAG, {
      uNoise: noise.source,
      uSky: sky.texture.source,
      lightUniforms: {
        uScreen: { value: new Float32Array([1, 1]), type: 'vec2<f32>' },
        uLight: { value: new Float32Array([1, 1, 1]), type: 'vec3<f32>' },
        uLamp: { value: new Float32Array([1.0, 0.86, 0.66]), type: 'vec3<f32>' },
        uNight: { value: 0, type: 'f32' },
        uDim: { value: 1, type: 'f32' },
        uVignette: { value: 1, type: 'f32' },
        uCam: { value: new Float32Array([0, 0, 1]), type: 'vec3<f32>' },
        uFlash: { value: new Float32Array([0, 0, 0, 1]), type: 'vec4<f32>' },
        uDrift: { value: new Float32Array([0, 0]), type: 'vec2<f32>' },
        uWeather: { value: 0, type: 'f32' },
      },
    });
    this.mesh = new Mesh({ geometry: quadGeometry({ x0: 0, y0: 0, x1: 1, y1: 1 }), shader: this.shader });
    this.mesh.blendMode = 'multiply';
  }

  resize(w: number, h: number): void {
    if (w === this.w && h === this.h) return;
    this.w = w;
    this.h = h;
    setQuad(this.mesh.geometry, { x0: 0, y0: 0, x1: w, y1: h });
    const s = this.shader.resources.lightUniforms.uniforms.uScreen as Float32Array;
    s[0] = w;
    s[1] = h;
  }

  set(color: [number, number, number], night: number, dim: number, vignette: number): void {
    const u = this.shader.resources.lightUniforms.uniforms;
    const l = u.uLight as Float32Array;
    l[0] = color[0];
    l[1] = color[1];
    l[2] = color[2];
    u.uNight = night;
    u.uDim = dim;
    u.uVignette = vignette;
  }

  /**
   * Cloud shadows for this frame; `flash` = lightning at a world point (x, y, strength 0..1,
   * radius in world units), strength 0 for none.
   */
  /**
   * Weather for this frame (`on` = the sky field holds this frame's clouds): cloud shadows from
   * the field, billows drifted by `drift`, and a lightning `flash` (x, y, strength, radius; world).
   */
  setSky(
    on: boolean,
    cam: { x: number; y: number; zoom: number },
    flash: [number, number, number, number],
    drift: { x: number; y: number },
  ): void {
    const u = this.shader.resources.lightUniforms.uniforms;
    u.uWeather = on ? 1 : 0;
    if (!on) return;
    (u.uDrift as Float32Array).set([drift.x, drift.y]);
    (u.uCam as Float32Array).set([cam.x, cam.y, cam.zoom]);
    (u.uFlash as Float32Array).set(flash);
  }

  destroy(): void {
    destroyMesh(this.mesh);
  }
}
